import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ChevronRight, Dumbbell, Clock, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL, analytics } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import LocationCard from "@/components/LocationCard";

export default function HomePage() {
  const [locations, setLocations] = useState([]);
  const [homepageContent, setHomepageContent] = useState({
    hero_headline: "UNLEASH YOUR GIANT POTENTIAL",
    hero_subheadline: "8 locations & growing strong across New Jersey and Philadelphia, Pennsylvania",
    promo_active: false,
    promo_text: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchHomepageContent();
    fetchLocations();
  }, []);

  // Non-blocking geolocation sort: runs once after locations first populate
  useEffect(() => {
    if (locations.length === 0 || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await axios.get(`${API_URL}/locations/nearest`, {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude, limit: 50 }
          });
          setLocations(res.data);
        } catch (_) {
          // keep default order
        }
      },
      () => {
        // denied/unavailable — keep default order silently
      }
    );
  }, [locations.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHomepageContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/cms/homepage`);
      setHomepageContent(response.data);
    } catch (error) {
      console.error("Failed to fetch homepage content:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_URL}/locations`);
      setLocations(res.data);
    } catch (e) {
      console.error("Failed to fetch locations:", e);
    }
  };

  const handleSignupClick = (location) => {
    analytics.trackSignupClick(location.id, location.name);
    if (location.signup_url) {
      window.open(location.signup_url, "_blank", "noopener,noreferrer");
      navigate("/join-thank-you");
    }
  };

  const amenities = [
    { icon: Dumbbell, label: "PREMIUM EQUIPMENT", desc: "Reliable. Strong. Proven." },
    { icon: Clock, label: "WORK OUT ON YOUR TIME", desc: "24/7 access at select locations" },
    { icon: Dumbbell, label: "STRENGTH & CARDIO", desc: "Full selection for every workout" },
    { icon: Activity, label: "SUPPORTIVE ENVIRONMENT", desc: "Women-only space designed for you" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HealthClub",
    "name": "Jersey Girls",
    "description": "Women-only fitness centers across New Jersey and Philadelphia. Supportive environment, premium equipment, free group strength training, and 24/7 access at select locations.",
    "url": "https://jerseygirlsclubs.com",
    "logo": "https://jerseygirlsclubs.com/images/Jersey-Girls-Logo-768x671.jpeg",
    "image": "https://jerseygirlsclubs.com/images/Jersey-Girls-Logo-768x671.jpeg",
    "telephone": "(856) 382-0555",
    "email": "contact@giantfitnessclubs.com",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "NJ",
      "addressCountry": "US"
    },
    "priceRange": "$$",
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "24/7 Access", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Premium Equipment", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Personal Training", "value": true }
    ],
    "sameAs": []
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title="Your Best Self Starts Here"
        description="Women-only fitness centers across New Jersey and Philadelphia. Supportive environment, premium equipment, free group strength training. Join Jersey Girls today!"
        url="/"
        structuredData={structuredData}
      />

      {/* Video Hero Section */}
      <section className="video-hero" data-testid="hero-section">
        {/* Video background — object-top anchors to top of frame, cropping the bottom banner */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-top"
          >
            <source src="/videos/jg-intro-video.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/75" />

        {/* Hero content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16">
          {/* Promo banner */}
          {homepageContent.promo_active && homepageContent.promo_text && (
            <div className="inline-block bg-brand-rose/20 border border-brand-rose/50 rounded-sm px-4 py-2 mb-6">
              <span className="text-brand-pink font-semibold text-sm uppercase tracking-wider">
                {homepageContent.promo_text}
              </span>
            </div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-headline text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-black text-white leading-tight mb-4"
            data-testid="hero-headline"
          >
            {homepageContent.hero_headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="hero-subheadline text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            {homepageContent.hero_subheadline}
          </motion.p>
        </div>
      </section>

      {/* Locations Grid Section */}
      {locations.length > 0 && (
        <section className="py-12 md:py-16 px-4 md:px-8 bg-white" data-testid="homepage-locations-section">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-black text-gray-900 mb-1">
                FIND YOUR FITNESS CENTER
              </h2>
              <p className="text-gray-600">
                Click a location to learn more, see hours, and sign up.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="homepage-locations-grid">
              {locations.map((location, index) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  index={index}
                  onSignupClick={location.signup_url ? handleSignupClick : undefined}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Amenities Section */}
      <section className="pt-12 pb-20 md:pt-16 md:pb-24 px-4 md:px-8 bg-white" data-testid="amenities-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-14"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
              For Every Body. Every Goal.
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto text-lg">
              World class facilities built for everyone
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {amenities.map((amenity, index) => (
              <motion.div
                key={amenity.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-light no-hover p-8 text-center"
                data-testid={`amenity-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-lg bg-brand-pink/10 flex items-center justify-center">
                  <amenity.icon className="w-8 h-8 text-brand-pink" />
                </div>
                <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">{amenity.label}</h3>
                <p className="text-gray-700 text-sm">{amenity.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Jersey Girls Section */}
      <section className="py-20 px-4 md:px-8 bg-gray-100" data-testid="jersey-girls-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="women-only-badge inline-block mb-4">Women Only</div>
              <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
                JERSEY GIRLS FITNESS
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Explore our women‑only fitness centers.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Jersey Girls is available at 7 of our 8 locations",
                  "Free Certified Personal Trainer led Group Strength Training Sessions",
                  "Child care at select locations"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-brand-pink" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/jersey-girls">
                <Button
                  className="bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider"
                  data-testid="jersey-girls-cta"
                >
                  Learn More
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="/images/Jersey-Girls-Logo-768x671.jpeg"
                  alt="Jersey Girls Fitness"
                  className="w-full h-full object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-gradient-to-b from-gray-50 to-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-6">
              READY TO START?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Find your nearest Jersey Girls location and begin your journey today.
            </p>
            <Link to="/locations">
              <Button
                size="lg"
                className="h-14 px-10 text-lg rounded-xl bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider shadow-soft hover:shadow-neon-pink hover:-translate-y-1 transition-all duration-200"
                data-testid="cta-find-location-btn"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Find Your Location
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
